/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import PatientFullForm from "components/forms/patient/PatientFullForm";
import Button from "components/generals/Button";
import Dialog from "components/generals/Dialog";
import DialogTitle from "components/generals/DialogTitle";
import DialogContent from "components/generals/DialogContent";
import DialogContentText from "components/generals/DialogContentText";
import DialogActions from "components/generals/DialogActions";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { CoreAPI_URLs } from "constants/http.const";
import { AccountingMovementInterface, QueryResultInterface, RoutesConstants } from "@malmur-technologies/medicalsystem-shared";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import useHttp from "hooks/http.hook";
import { TableProps } from "interfaces";
import { HeaderEnum, PatientInterface, PersonRelationInterface } from "@malmur-technologies/medicalsystem-shared";
import { FC, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { calculateAge } from "utils/generic.utils";
import { getNewPatientDataContent } from "utils/model.utils";
import { calculateAccountingTotals } from "utils/accountingMovements.utils";

//TODO: CHECK SEARCH LAST CREATED PATIENT WITH 10+ PAITENTS REGISTERED

/**
 * Patient table component for displaying and managing patient records in the system.
 * 
 * Features:
 * - Displays patient information in sortable, paginated data table
 * - Columns: name, last name, sex, age (calculated from DOB), phone, action buttons
 * - Edit patient functionality via modal with PatientFullForm
 * - Delete patient with confirmation dialog and optimistic UI update
 * - Navigate to patient hub for detailed patient view
 * - Permission-based access control for edit/delete operations
 * - Age calculation from date of birth
 * - Support for parent/guardian relationships for minor patients
 * - Search functionality (TODO: investigate search with 10+ patients)
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure patient identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates entity permissions before delete operations
 * 
 * @param {TableProps<PatientInterface>} props - Table properties
 * @param {PatientInterface[]} props.modelList - Array of patient records to display
 * @param {Function} props.setModelList - Callback to update patient list after changes
 * 
 * @returns {JSX.Element} Patient data table with edit, delete, and view hub actions
 */
const PatientTable: FC<TableProps<PatientInterface>> = (props) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [selectedPatient, setSelectedPatient] = useState<PatientInterface>(
    getNewPatientDataContent()
  );
  const [selectedParentPatient, setSelectedParentPatient] = useState<PersonRelationInterface[]>();
  const [activeEditPatientModal, setActiveEditPatientModal] = useState<boolean>(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(false);
  const [showDebtErrorModal, setShowDebtErrorModal] = useState<boolean>(false);
  const [patientToDelete, setPatientToDelete] = useState<PatientInterface | null>(null);

  const { t } = useTranslation();

  const navigate = useNavigate();

  useEffect(() => {
    if (selectedPatient && selectedPatient.id) {
      const updatedPatient = props.modelList.find(p => p.id === selectedPatient.id);
      if (updatedPatient) {
        setSelectedPatient(updatedPatient);
      }
    }
  }, [props.modelList]);

  let age: number;

  function handleOpenPatientHub(id: string) {
    const data = id;
    navigate(RoutesConstants.PATIENT_HUB, { state: { data } });
  }


  //Verify if patient has pending debt before initiating delete flow
  const initiateDeleteFlow = (patient: PatientInterface) => {
    setPatientToDelete(patient);
    const endpoint = `${CoreAPI_URLs.ACCOUNTING_MOVEMENT_GENERAL_OPERATIONS}/all`;
    sendRequest(
      endpoint,
      (response: QueryResultInterface<AccountingMovementInterface>) => checkBalanceAndShowModal(response),
      "GET",
      undefined,
      [{ header: HeaderEnum.PATIENT_ID, value: patient.id }]
    );
  };

  const checkBalanceAndShowModal = (
    accountingMovements: QueryResultInterface<AccountingMovementInterface>
  ) => {
    if (!accountingMovements || !accountingMovements.results || accountingMovements.results.length === 0) {
      setShowDeleteConfirmModal(true);
      return;
    }

  const balancePatient = calculateAccountingTotals(accountingMovements.results);
    if (balancePatient.balancePatient > 0) {
      setShowDebtErrorModal(true);
    } else {
      setShowDeleteConfirmModal(true);
    }
  };

  const confirmDeletePatient = () => {
    if (!patientToDelete) return;
    
    sendRequest<boolean, { id: string }>(
      CoreAPI_URLs.PATIENTS_GENERAL_OPERATIONS,
      onPatientDeleteHandler,
      "DELETE",
      null,
      [{ header: HeaderEnum.RESOURCE_ID, value: patientToDelete.id }],
      { successMessage: t("patientTable.patientDeleted"), payload: { id: patientToDelete.id } }
    );
    setShowDeleteConfirmModal(false);
    setPatientToDelete(null);
  };
  
  const onPatientDeleteHandler = (result: boolean, payload: { id?: string }) => {
    if (!result) {
      return;
    }
    if (!payload || !payload.id) return;
    props.setModelList((prev) => prev.filter((p) => p.id !== payload.id));
  };

  const getUITable = (list: PatientInterface[]) => {
    return {
      columns: [
        {
          Header: t("patientTable.name"),
          accessor: "person.name",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("patientTable.lastName"),
          accessor: "person.lastname",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("patientTable.sex"),
          accessor: "person.sex",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value ? t(`patientTable.${value}`) : ''} />,
        },
        {
          Header: t("patientTable.age"),
          accessor: "person.dob",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => {
            age = calculateAge(value);
            return <DefaultCell value={age.toString()} />;
          },
        },
        {
          Header: t("patientTable.action"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <>
              <Button
                name="viewPatientButton"
                variant="contained"
                color="info"
                sx={{ marginRight: 1 }}
                onClick={() => {
                  const patient = list.find((p) => p.id === value);
                  if (patient) {
                    openModalHandler(patient);
                  }
                }}
              >
                {t("patientTable.view")}
              </Button>

              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW]}
              >
                <Button
                  name="hubPatientButton"
                  variant="contained"
                  color="info"
                  sx={{ marginRight: 1 }}
                  onClick={() => {
                    const patient = list.find((p) => p.id === value);
                    if (patient) {
                      handleOpenPatientHub(patient.id);
                    }
                  }}
                >
                  {t("patientTable.HUB")}
                </Button>
              </PermissionWrapper>

              <PermissionWrapper
                requiredPermissions={[PermissionsUser[EntityPermissions.PATIENTS].DELETE]}
              >
                <Button
                  name="deletePatientButton"
                  variant="contained"
                  color="error"
                  onClick={() => {
                    const patient = list.find((p) => p.id === value);
                    if (patient) {
                      initiateDeleteFlow(patient);
                    }
                  }}
                >
                  {t("patientTable.deleteAction")}
                </Button>
              </PermissionWrapper>
            </>
          ),
        },
      ],

      rows: list,
    };
  };

  const openModalHandler = async (patient: PatientInterface) => {
    setSelectedPatient(patient);
    age = calculateAge(patient.person.dob);
    
    await sendRequest<PersonRelationInterface[]>(
      CoreAPI_URLs.PERSONS_RELATION_GENERAL_OPERATIONS + "/by-grantor/id",
      (response) => {
        if (response) {
          setSelectedParentPatient(response);
        } else {
          setSelectedParentPatient([]);
        }
      },
      "GET",
      undefined,
      [{ header: HeaderEnum.RESOURCE_ID, value: patient.person.id }]
    );
    setActiveEditPatientModal(true);
  };

  const dataTableData = useMemo(() => getUITable(props.modelList), [props.modelList]);

  return (
    <>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}
        onSortChange={props.onSortChange}
        currentSortBy={props.currentSortBy}
        currentSortOrder={props.currentSortOrder}
      />

      <Modal
        open={activeEditPatientModal}
        onClose={() => {
          setActiveEditPatientModal(false);
        }}
      >
        <PatientFullForm
          isModal={true}
          modelList={props.modelList}
          setModelList={props.setModelList}
          model={selectedPatient}
          parentsRelationList={selectedParentPatient}
          action={FormActionType.MODIFY}
          onCloseAction={() => setActiveEditPatientModal(false)}
        />
      </Modal>

      <Dialog
        open={showDebtErrorModal}
        onClose={() => {
          setShowDebtErrorModal(false);
          setPatientToDelete(null);
        }}
        aria-labelledby="debt-error-dialog-title"
        aria-describedby="debt-error-dialog-description"
      >
        <DialogTitle id="debt-error-dialog-title">
          {t("patientTable.cannotDeleteTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="debt-error-dialog-description">
            {t("patientTable.cannotDeleteDebt")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            onClick={() => {
              setShowDebtErrorModal(false);
              setPatientToDelete(null);
            }}
            name="cy-ok"
          >
            {t("patientTable.ok")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showDeleteConfirmModal}
        onClose={() => {
          setShowDeleteConfirmModal(false);
          setPatientToDelete(null);
        }}
        aria-labelledby="delete-confirm-dialog-title"
        aria-describedby="delete-confirm-dialog-description"
      >
        <DialogTitle id="delete-confirm-dialog-title">
          {t("patientTable.deleteConfirmation")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-confirm-dialog-description">
            {t("patientTable.deleteDescription")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowDeleteConfirmModal(false);
              setPatientToDelete(null);
            }}
            color="secondary"
            name="cy-cancel"
          >
            {t("patientTable.cancel")}
          </Button>
          <Button
            onClick={confirmDeletePatient}
            color="primary"
            autoFocus
            name="cy-confirm"
          >
            {t("patientTable.confirmDelete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PatientTable;